import { Button, FormLayout, TextInput } from 'components/common'
import SwitchField from 'components/bundleProduct/switchField'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ConfigurationWithId } from 'types/configuration'
import { getInputErrorMessage } from 'utils/helpers/getNestedErrorMessage'
import { handleError } from 'utils/helpers/handleError'
import ManualTalabatCrons from './manualTalabatCrons'
import TalabatAggregatorChains from './talabatAggregatorChains'
import TalabatPriorityStoreSection from './talabatPriorityStoreSection'
import { talabatConfigurationFields } from './utils'
import {
  talabatApiSchema,
  TalabatConfigFormFields,
} from 'utils/schema/talabatConfigSchema'
import { yupResolver } from '@hookform/resolvers/yup'
import { updateTalabatConfig } from 'utils/api/configApi'

const {
  IS_ACTIVE,
  AUTH_URL,
  WEBHOOK_AUTH_TOKEN,
  ORDER_WEBHOOK_TOKEN,
  API_BASE_URL,
  BATCH_SIZE,
  ORDER_LOOKUP_WINDOW_MINUTES,
  AGGREGATORS,
} = talabatConfigurationFields

type Props = {
  configuration?: ConfigurationWithId
}

const TalabatConfigurationForm = ({ configuration }: Props) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TalabatConfigFormFields>({
    resolver: yupResolver(talabatApiSchema),
    defaultValues: {
      ...(configuration?.talabatApi || {}),
    },
  })

  const [loading, setLoading] = useState(false)

  const aggregators = watch(AGGREGATORS.key) || []

  const handleChangeConfigurations = async (data: any) => {
    const configToast = toast.loading('Updating Talabat config...')
    setLoading(true)

    try {
      await updateTalabatConfig(data)
      toast.success('Talabat config updated successfully.', {
        id: configToast,
      })
    } catch (error) {
      handleError(error, configToast, {
        default: 'Failed to update Talabat config.',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(handleChangeConfigurations)}>
      {/* ================= API ================= */}
      <h3 className="title mt-4">API Configuration</h3>

      <FormLayout>
        {/* Top Section: Toggle */}
        <SwitchField
          control={control}
          name={IS_ACTIVE.key}
          label={IS_ACTIVE.label}
          description="Enable or disable Talabat API integration"
          defaultValue={true}
          showStatusPill
        />

        {/* Divider */}
        <div className="mb-5 border-t" />

        {/* Inputs */}
        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={API_BASE_URL.key}
            label={API_BASE_URL.label}
            error={getInputErrorMessage(errors, API_BASE_URL)}
            placeholder="https://api.talabat.com"
          />

          <TextInput
            required
            type="number"
            register={register}
            name={BATCH_SIZE.key}
            label={BATCH_SIZE.label}
            error={getInputErrorMessage(errors, BATCH_SIZE)}
            placeholder="e.g., 10000"
          />

          <TextInput
            required
            type="number"
            register={register}
            name={ORDER_LOOKUP_WINDOW_MINUTES.key}
            label={ORDER_LOOKUP_WINDOW_MINUTES.label}
            error={getInputErrorMessage(errors, ORDER_LOOKUP_WINDOW_MINUTES)}
            placeholder="e.g., 10"
          />
        </div>
      </FormLayout>

      <h3 className="title mt-3">Auth Configuration</h3>
      <FormLayout>
        <div className="grid gap-4 mid-md:grid-cols-2">
          <TextInput
            required
            register={register}
            name={AUTH_URL.key}
            label={AUTH_URL.label}
            error={getInputErrorMessage(errors, AUTH_URL)}
          />

          <TextInput
            required
            type="password"
            register={register}
            name={WEBHOOK_AUTH_TOKEN.key}
            label={WEBHOOK_AUTH_TOKEN.label}
            error={getInputErrorMessage(errors, WEBHOOK_AUTH_TOKEN)}
          />

          <TextInput
            required
            type="password"
            register={register}
            name={ORDER_WEBHOOK_TOKEN.key}
            label={ORDER_WEBHOOK_TOKEN.label}
            error={getInputErrorMessage(errors, ORDER_WEBHOOK_TOKEN)}
          />
        </div>
      </FormLayout>

      <h3 className="title mt-4">Aggregator Chain Config</h3>
      <p className="mb-3 text-xs text-gray-500">
        Each aggregator can have multiple chains. Every chain has its own Chain
        ID and its own explicit list of stores — stores are no longer derived
        from the aggregator&apos;s general API store list.
      </p>

      <div className="flex flex-col gap-4">
        {aggregators.map((agg, index: number) => (
          <FormLayout key={agg.id}>
            <TalabatAggregatorChains
              aggregatorIndex={index}
              aggregatorId={agg.id}
              vendorMapKey={agg.vendorMapKey}
              register={register}
              control={control}
              setValue={setValue}
              errors={errors}
            />
          </FormLayout>
        ))}
      </div>

      <TalabatPriorityStoreSection
        register={register}
        setValue={setValue}
        priorityStores={configuration?.talabatApi?.priorityStores}
        errors={errors}
      />

      <Button loading={loading} buttonText="Save" className="mt-5 w-32" />

      <ManualTalabatCrons />
    </form>
  )
}

export default TalabatConfigurationForm
